<template>
  <base-page-layout title="Consultar ofertas">
    <mf-button label="Voltar" color="marca-0-l40" icon="arrow_back" icon-position="left" text @click="goBack" />
    <div class="d-flex flex-column justify-center align-center my-2 mx-4">
      <span class="gray-title ma-2">Ofertas e descontos</span>
      <span class="gray-subtitle mx-3"
        >Entre com os dados para identificar o consumidor, data da compra, loja e produtos das ofertas que deseja consultar.</span
      >

      <form class="d-flex flex-column ma-6 responsive-width">
        <v-radio-group v-model="searchConsumerFor" mandatory>
          <template v-slot:label>
            <div>Buscar consumidor por:</div>
          </template>
          <v-radio label="Documento" value="document"></v-radio>
          <v-radio label="Telefone" value="phone"></v-radio>
        </v-radio-group>

        <label class="cinza-0-text">{{ consumerLabel }}</label>
        <v-text-field
          v-model="consumer"
          v-mask="inputMask"
          :error-messages="consumerErrors"
          :placeholder="consumerPlaceholder"
          required
          outlined
          background-color="white"
          :disabled="loading"
          @keydown.enter="submit"
          @input="$v.consumer.$touch()"
          @blur="$v.consumer.$touch()"
        />

        <label class="cinza-0-text">Data:</label>
        <date-select
          v-model="date"
          type="date"
          placeholder="Selecionar data"
          :max="maxDate"
          :error="dateError.length > 0"
          :error-messages="dateError"
          :disabled="loading"
          hide-action-buttons
          @change="() => (dateError = [])"
        />

        <label class="cinza-0-text">Loja:</label>
        <div style="background-color: white; height: 56px">
          <mf-select-units-dialog
            outlined
            required
            :unit-error="unitError"
            :selected-units.sync="selectedUnits"
            :selected-units-configuration.sync="units_configuration"
            :units="units"
            :tag-list="tagList"
            :group-list="unitsGroups"
            :default-data-type="defaultDataType"
            :disabled="loading"
            single-select
            hide-ecommerce-configuration
            @change="() => (unitError = false)"
          />
        </div>
        <mf-info-card
          class="mt-2"
          description="A seleção de data e lojas inclui apenas ofertas válidas na loja e data escolhida, mas o consumo exibido é a soma total do consumo da oferta pelo consumidor selecionado nas lojas e no período onde ela está vigente."
        />

        <div class="d-flex flex-column align-center mt-5" style="gap: 6px">
          <select-products-dialog
            ref="productsDialog"
            :selected-products.sync="selectedProducts"
            :error="productsError"
            :disabled="loading"
            @open="() => (productsError = false)"
          />
          <span v-if="productsError" style="color: #ff5252; font-size: 12px">Selecione ao menos um produto</span>
          <span
            v-if="!productsError && selectedProducts && selectedProducts.length > 0"
            class="link"
            style="font-size: 16px !important"
            @click="openProductsDialog"
            >Ver {{ productMessage }}</span
          >
        </div>

        <mf-button
          class="mt-8 align-self-center"
          size="lg"
          :loading="loading"
          :disabled="loading || disabledSubmit"
          color="marca-0-l40"
          label="Buscar"
          icon="arrow_forward"
          icon-position="right"
          @click="submit"
        />
      </form>
    </div>
    <select-consumer-dialog ref="consumerDialog" :consumers="consumers" @selectConsumer="goToResults" />
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import { mapGetters } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'
import DateSelect from '@/components/DateSelect.vue'
import SelectProductsDialog from '@/components/select-products/SelectProductsDialog.vue'
import { QUERY_USERS_WITH_TEMP } from '../../graphql'
import { DateTime } from 'luxon'
import SelectConsumerDialog from '../components/SelectConsumerDialog.vue'

export default {
  components: {
    BasePageLayout,
    DateSelect,
    SelectProductsDialog,
    SelectConsumerDialog,
  },
  data: () => ({
    loading: false,
    consumer: '',
    searchConsumerFor: 'document',
    date: DateTime.now().toISODate(),
    dateError: [],
    selectedUnits: [],
    unitError: false,
    units_configuration: [],
    selectedProducts: [],
    productsError: false,
    consumers: [],
  }),
  computed: {
    ...mapGetters(['units', 'tagList', 'unitsGroups']),

    consumerErrors() {
      const errors = []
      if (!this.$v.consumer.$dirty) return errors
      !this.$v.consumer.required && errors.push('Preencha o campo com CPF, CNPJ ou telefone')
      !this.$v.consumer.minLength && errors.push('CPF, CNPJ ou telefone inválido')
      return errors
    },
    inputMask() {
      if (this.searchConsumerFor === 'phone') return ['(##) #####-####']
      else return ['###.###.###-##', '##.###.###/####-##']
    },
    consumerLabel() {
      if (this.searchConsumerFor === 'phone') return 'Telefone:'
      else return 'Nro. Documento:'
    },
    consumerPlaceholder() {
      if (this.searchConsumerFor === 'phone') return 'Número do telefone'
      else return 'CPF ou CNPJ'
    },
    selectedUnitFull() {
      if (this.selectedUnits.length > 0) return this.units.find((unit) => unit.id_erp === this.selectedUnits[0])
      else return { name: '', id_erp: '' }
    },
    defaultDataType() {
      if (this.campaign?.units_configuration?.length) {
        return this.campaign?.units_configuration[0]?.dataType
      }
      return null
    },
    productMessage() {
      if (this.selectedProducts.length === 1) return '1 produto selecionado'
      else return `${this.selectedProducts.length} produtos selecionados`
    },
    maxDate() {
      return DateTime.now().toISODate()
    },
    disabledSubmit() {
      if (this.$v.$invalid) return true
      if (!this.date || this.date.length <= 0) {
        return true
      }
      if (this.selectedUnits && this.selectedUnits.length <= 0) {
        return true
      }
      if (this.selectedProducts && this.selectedProducts.length <= 0) {
        return true
      }
      return false
    },
  },
  async mounted() {
    this.selectedUnits = JSON.parse(window.localStorage.getItem('consult-offer-unit')) || []
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'consultar-ofertas' })
    },
    openProductsDialog() {
      this.$refs.productsDialog.selectProductsDialog = true
    },
    clearErrors() {
      this.dateError = []
      this.unitError = false
      this.productsError = false
    },
    invalidInput() {
      this.clearErrors()
      let error = false
      this.$v.$touch()
      if (this.$v.$invalid) error = true
      if (!this.date || this.date.length <= 0) {
        this.dateError.push('Período é obrigatório')
        error = true
      }
      if (this.selectedUnits && this.selectedUnits.length <= 0) {
        this.unitError = true
        error = true
      }
      if (this.selectedProducts && this.selectedProducts.length <= 0) {
        this.productsError = true
        error = true
      }
      return error
    },
    async submit() {
      if (this.invalidInput()) return
      window.localStorage.setItem('consult-offer-unit', JSON.stringify(this.selectedUnits))

      this.loading = true
      try {
        const result = await this.$apollo.query({
          query: QUERY_USERS_WITH_TEMP,
          fetchPolicy: 'no-cache',
          variables: {
            search: this.onlyNumbers(this.consumer),
          },
          context: {
            uri: this.$microservicesUrls['consumer'],
          },
        })
        this.consumers = result.data.usersWithTemp
        if (this.consumers.length === 1) {
          this.goToResults(this.consumers[0])
        } else {
          this.$refs.consumerDialog.selectConsumerDialog = true
        }
      } catch (err) {
        console.log(err)
        this.$snackbar({ message: 'Erro ao buscar consumidor', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    onlyNumbers(string) {
      return string.replace(/\D/g, '')
    },
    goToResults(consumerSelected) {
      this.$router.push({
        name: 'consultar-ofertas-descontos-resultado',
        params: {
          c: consumerSelected,
          d: this.date,
          u: this.selectedUnitFull,
          p: this.selectedProducts,
        },
      })
    },
  },
  validations: {
    consumer: { required, minLength: minLength(11) },
  },
}
</script>

<style lang="scss" scoped>
.gray-title {
  color: $cinza-d20;
  font-size: 22px;
  line-height: 26.4px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  text-align: center;
}

.gray-subtitle {
  color: $cinza-d10;
  font-size: 16px;
  font-weight: 400;
  line-height: 19.2px;
  letter-spacing: 0.4px;
  text-align: center;
}

.cinza-0-text {
  color: $cinza-0;
}

.responsive-width {
  width: 40%;
}

@media screen and (max-width: 933px) {
  .responsive-width {
    width: 100%;
  }
}
</style>
